import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Types } from '@cornerstonejs/core';
import { Dicom } from '@/models/dicom';
import { useSettings } from '@/features/settings';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearMeasurement,
  selectActiveMeasurement,
} from 'state/slices/measurementSession';
import { useGetStudyReportsQuery } from '@/state/slices/reports';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import { usePermissions } from '@/hooks/usePermissions';
import { registerViewerNavigator } from '@/features/study-detail/history/viewerNavigation';
import {
  ViewerContext,
  GridLayout,
  ViewerContextValue,
  ViewportInitialCamera,
} from './ViewerContext';
import { PlaybackContext, PlaybackContextValue } from './PlaybackContext';
import { ViewerLoadingProvider } from './ViewerLoadingContext';
import { useSharedRenderingEngine } from './hooks/useSharedRenderingEngine';
import {
  useSharedToolState,
  MANIPULATION_TOOLS,
} from './hooks/useSharedToolState';
import { useVideoSignalManager } from './hooks/useVideoSignalManager';
import { useAdjacentDicomPreloader } from './hooks/useAdjacentDicomPreloader';
import { useBlockedNavigationNotice } from './hooks/useBlockedNavigationNotice';
import {
  useViewerUrlSync,
  ViewerUrlStateChangeHandler,
} from './hooks/useViewerUrlSync';
import { resizeAndRefitViewports } from './utils/engineResize';
import {
  fillUnpinnedPanes,
  getCursorPosition,
  getPinnedUuids,
  listNavigableIndices,
  listUnpinnedPanes,
  reconcilePinsForViewportCount,
  stepCursor,
} from './utils/paneNavigation';
import {
  getInitialPaneState,
  ResolvedPaneState,
  toGridLayout,
  ViewerUrlState,
} from './utils/viewerUrlState';
import {
  filterAndPrioritizeDicoms,
  listStudyViews,
} from './viewFiltering/viewTaxonomy';

interface ViewerProviderProps {
  studyUuid: string;
  dicoms: Dicom[];
  children: React.ReactNode;
  goToAnnotationRef?: React.MutableRefObject<
    ((dicomUuid: string, frameNumber: number) => void) | null
  >;
  urlState?: ViewerUrlState;
  onUrlStateChange?: ViewerUrlStateChangeHandler;
}

/**
 * ViewerProvider - Provides viewer context to children without rendering any UI.
 * This allows DicomList and DicomButtons to access viewer state while maintaining
 * the original layout structure.
 */
export function ViewerProvider({
  studyUuid,
  dicoms,
  children,
  goToAnnotationRef,
  urlState,
  onUrlStateChange,
}: ViewerProviderProps) {
  // Sort DICOMs by index once — must match DicomList's sort order
  // so that global indices are consistent between thumbnails and viewports.
  const sortedDicoms = useMemo(
    () => [...dicoms].sort((a, b) => a.index - b.index),
    [dicoms]
  );

  // 1-based display number per DICOM uuid; drives the URL and clip badges.
  const dicomNumberByUuid = useMemo(
    () => new Map(sortedDicoms.map((d, i) => [d.uuid, i + 1])),
    [sortedDicoms]
  );

  // View filtering (AP-441): selected view labels + clinical group. The filter
  // is applied here — before pinning/pagination — so the grid and the filmstrip
  // share one filtered, tier-prioritized list. When filtering changes the set,
  // the cursor is clamped and the unpinned panes refill (see paneSource below).
  const [selectedViews, setSelectedViews] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const filteredSortedDicoms = useMemo(
    () =>
      filterAndPrioritizeDicoms(sortedDicoms, selectedViews, selectedGroupId),
    [sortedDicoms, selectedViews, selectedGroupId]
  );
  const availableViews = useMemo(
    () => listStudyViews(sortedDicoms),
    [sortedDicoms]
  );

  const { settings } = useSettings();
  const dispatch = useDispatch();

  // A URL layout is not saved to settings.
  const [gridLayout, setGridLayout] = useState<GridLayout>(
    () => urlState?.layout ?? toGridLayout(settings.panelLayout)
  );

  const [activeViewportId, setActiveViewportId] = useState<string | null>(null);
  const [initialPaneState] = useState(() =>
    getInitialPaneState(
      urlState,
      sortedDicoms,
      filteredSortedDicoms,
      gridLayout.rows * gridLayout.cols
    )
  );
  const [selectedViewportIndex, setSelectedViewportIndex] = useState<number>(
    initialPaneState.selectedViewportIndex
  );
  // Navigation cursor: index into filteredSortedDicoms where normal navigation
  // starts filling the unpinned panes. A free anchor (not a fixed page number)
  // so a filmstrip click can re-seat the clicked DICOM into the first unpinned
  // pane. It moves ONLY on a successful normal navigation — never on pin/unpin,
  // Shift+drag, a blocked attempt (AP-804), or restoring URL state.
  const [navCursor, setNavCursor] = useState<number>(
    initialPaneState.navCursor
  );
  // What each pane displays, independent of the cursor. Only normal navigation
  // (unpinned panes), Shift+drag (target pane) and URL state write to it.
  const [assignedDicoms, setAssignedDicoms] = useState<(Dicom | null)[]>(
    initialPaneState.paneDicoms
  );
  // Pane slots protected from normal navigation. Toggling a pin never changes
  // pane content, the active pane, or the cursor.
  const [pinnedSlots, setPinnedSlots] = useState<ReadonlySet<number>>(
    initialPaneState.pinnedSlots
  );
  const [autoplayEnabled, setAutoplayEnabled] = useState(settings.autoplay);
  const [startFrame, setStartFrame] = useState<number | undefined>(undefined);
  // Per-pane playback speed factor (1 = native). Every cine loads at its native
  // rate; the cine FPS control adjusts only the selected pane, so each pane
  // keeps its own rate and panes without an explicit entry stay at native.
  const [cineSpeedByPane, setCineSpeedByPane] = useState<Map<number, number>>(
    () => new Map()
  );
  // Resolved native FPS per pane slot, reported by each ViewerPane once its
  // video loads (so the cine FPS control can anchor to the selected pane).
  const [nativeFpsByPane, setNativeFpsByPane] = useState<Map<number, number>>(
    () => new Map()
  );

  // Per-DICOM VOI persistence map. Keyed by DICOM UUID, stores the last
  // user-applied voiRange so it can be restored on DICOM re-navigation.
  // Ref (not state) because VOI changes are high-frequency during drag
  // and only need reading at discrete capture/restore moments.
  const voiMapRef = useRef<Map<string, { lower: number; upper: number }>>(
    new Map()
  );

  // Per-viewport initial camera snapshot for deterministic reset.
  // Saved after useViewportSetup completes; restored by resetViewport().
  const viewportInitialCameraRef = useRef<Map<string, ViewportInitialCamera>>(
    new Map()
  );

  // Track actual Cornerstone viewport IDs (including instance IDs)
  // Use ref to avoid re-renders when viewports register/unregister
  const registeredViewportIdsRef = useRef<Set<string>>(new Set());
  const [registeredViewportIds, setRegisteredViewportIds] = useState<string[]>(
    []
  );

  const {
    renderingEngine,
    renderingEngineId,
    isInitialized,
    error: engineError,
  } = useSharedRenderingEngine();

  const viewportCount = gridLayout.rows * gridLayout.cols;
  const viewportIds = useMemo(
    () => Array.from({ length: viewportCount }, (_, i) => `viewport-${i}`),
    [viewportCount]
  );

  // Register/unregister actual Cornerstone viewport IDs
  // Use ref for immediate access, state for reactivity when tools need to iterate
  const registerViewportId = useCallback((viewportId: string) => {
    if (registeredViewportIdsRef.current.has(viewportId)) return;
    registeredViewportIdsRef.current.add(viewportId);
    // Batch state update - only update state after a short delay to avoid cascading renders
    // during initial mount when multiple viewports register
    queueMicrotask(() => {
      setRegisteredViewportIds(Array.from(registeredViewportIdsRef.current));
    });
  }, []);

  const unregisterViewportId = useCallback((viewportId: string) => {
    if (!registeredViewportIdsRef.current.has(viewportId)) return;
    registeredViewportIdsRef.current.delete(viewportId);
    queueMicrotask(() => {
      setRegisteredViewportIds(Array.from(registeredViewportIdsRef.current));
    });
  }, []);

  const activeMeasurement = useSelector(selectActiveMeasurement);

  // Video signal manager for coordinating video pause/toggle across viewports
  const {
    subscribeToVideoPause,
    subscribeToVideoToggle,
    pauseAll,
    togglePlayPauseAll,
  } = useVideoSignalManager();

  const { data: reportsPagination } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });
  const reportIsFinalized = !!reportsPagination?.results?.[0]?.isFinalized;
  const { canEdit } = usePermissions({ isFinalized: reportIsFinalized });

  const { data: canonicalMeasurements } =
    useGetWorksheetCanonicalMeasurementsQuery();
  const activeMeasurementAbbreviation = useMemo(() => {
    const uuid = activeMeasurement?.canonicalMeasurement;
    if (!uuid) return null;
    return (
      canonicalMeasurements?.find((c) => c.uuid === uuid)?.abbreviation ?? null
    );
  }, [activeMeasurement?.canonicalMeasurement, canonicalMeasurements]);

  const {
    activeTool,
    changeTool,
    deactivateTool,
    setupToolGroupForViewport,
    cleanupToolGroupForViewport,
  } = useSharedToolState({
    activeViewportId,
    renderingEngineId,
    renderingEngine,
    viewportIds: registeredViewportIds, // Use actual registered IDs for tool management
    activeMeasurementLabel: activeMeasurement?.measurementLabel,
    activeMeasurementAbbreviation,
    canEdit,
  });

  const getPaneCineSpeed = useCallback(
    (paneIndex: number) => cineSpeedByPane.get(paneIndex) ?? 1,
    [cineSpeedByPane]
  );

  const setPaneCineSpeed = useCallback(
    (paneIndex: number, multiplier: number) => {
      setCineSpeedByPane((prev) => {
        const next = new Map(prev);
        next.set(paneIndex, multiplier);
        return next;
      });
    },
    []
  );

  const getPaneNativeFps = useCallback(
    (paneIndex: number) => nativeFpsByPane.get(paneIndex) ?? 0,
    [nativeFpsByPane]
  );

  const setPaneNativeFps = useCallback((paneIndex: number, fps: number) => {
    setNativeFpsByPane((prev) => {
      if (prev.get(paneIndex) === fps) return prev;
      const next = new Map(prev);
      next.set(paneIndex, fps);
      return next;
    });
  }, []);

  // The filtered list or grid size changed (View Filter, layout swap): drop pins
  // outside the grid, clamp the cursor, and refill the unpinned panes. Adjusted
  // during render (React's "store info from previous renders" pattern) so the
  // panes never commit with a stale length. A layout swap keeps the active
  // clip (AP-875): an unpinned selected pane's clip leads the new grid.
  const [paneSource, setPaneSource] = useState({
    dicoms: filteredSortedDicoms,
    viewportCount,
  });
  if (
    paneSource.dicoms !== filteredSortedDicoms ||
    paneSource.viewportCount !== viewportCount
  ) {
    const resized = paneSource.viewportCount !== viewportCount;
    const reconciled = reconcilePinsForViewportCount(
      assignedDicoms,
      pinnedSlots,
      viewportCount
    );
    const active =
      resized && !pinnedSlots.has(selectedViewportIndex)
        ? assignedDicoms[selectedViewportIndex]
        : null;
    const activeCursor = active
      ? filteredSortedDicoms.findIndex((d) => d.uuid === active.uuid)
      : -1;
    const cursor =
      activeCursor !== -1
        ? activeCursor
        : Math.min(navCursor, Math.max(0, filteredSortedDicoms.length - 1));
    setPaneSource({ dicoms: filteredSortedDicoms, viewportCount });
    if (reconciled.pinnedSlots !== pinnedSlots)
      setPinnedSlots(reconciled.pinnedSlots);
    if (cursor !== navCursor) setNavCursor(cursor);
    // Land on a pane that still pages, not on a surviving pin. Playback intent
    // is left alone: a pane that keeps its clip must not reload off its frame.
    if (resized) {
      setSelectedViewportIndex(
        listUnpinnedPanes(viewportCount, reconciled.pinnedSlots)[0] ?? 0
      );
    }
    setAssignedDicoms(
      fillUnpinnedPanes(
        reconciled.paneDicoms,
        viewportCount,
        reconciled.pinnedSlots,
        filteredSortedDicoms,
        cursor
      )
    );
  }

  // Map of pane slot → pinned Dicom, derived for consumers (grid, filmstrip).
  const pinnedPanes = useMemo(() => {
    const map = new Map<number, Dicom>();
    pinnedSlots.forEach((slot) => {
      const dicom = assignedDicoms[slot];
      if (dicom) map.set(slot, dicom);
    });
    return map;
  }, [assignedDicoms, pinnedSlots]);

  const pinnedUuids = useMemo(
    () => getPinnedUuids(assignedDicoms, pinnedSlots),
    [assignedDicoms, pinnedSlots]
  );

  // Normal navigation needs a visible, unpinned pane; null = fully pinned grid.
  const unpinnedPanes = listUnpinnedPanes(viewportCount, pinnedSlots);
  const availablePane = unpinnedPanes[0] ?? null;
  const allPanesPinned = availablePane === null;
  const pageSize = Math.max(1, unpinnedPanes.length);
  const reportBlockedNavigation = useBlockedNavigationNotice();

  // Pagination/preload operate on the FILTERED list so the View Filter and
  // View Group apply to the grid and filmstrip alike. DICOMs shown in pinned
  // panes are an explicit user override and are skipped by paging.
  const navigableIndices = useMemo(
    () => listNavigableIndices(filteredSortedDicoms, pinnedUuids),
    [filteredSortedDicoms, pinnedUuids]
  );
  const navigableDicoms = useMemo(
    () => navigableIndices.map((i) => filteredSortedDicoms[i]),
    [navigableIndices, filteredSortedDicoms]
  );

  // Page number derived from the free anchor — exposed for the filmstrip
  // auto-scroll trigger and adjacent-group preloading.
  const currentGroupIndex = useMemo(
    () => Math.floor(getCursorPosition(navigableIndices, navCursor) / pageSize),
    [navigableIndices, navCursor, pageSize]
  );

  // Preload video bytes for adjacent DICOM groups for faster switching.
  // Uses navigableDicoms + pageSize so preloaded content matches what
  // paging will actually display.
  useAdjacentDicomPreloader({
    studyUuid,
    dicoms: navigableDicoms,
    currentGroupIndex,
    viewportCount: pageSize,
    preloadRange: 1,
  });

  // Each page shows exactly pageSize new DICOMs in the unpinned pane slots.
  const totalGroups =
    navigableDicoms.length === 0
      ? 1
      : Math.ceil(navigableDicoms.length / pageSize);

  // UUIDs of DICOMs currently shown in non-pinned pane slots — consumed by DicomList
  // to highlight exactly those thumbnails in blue (without re-deriving from raw indices).
  const activeDicomUuids = useMemo(() => {
    const set = new Set<string>();
    assignedDicoms.forEach((d, i) => {
      if (d && !pinnedSlots.has(i)) set.add(d.uuid);
    });
    return set;
  }, [assignedDicoms, pinnedSlots]);

  // Enabled only when a pane can receive the DICOM AND there is somewhere to go.
  const canGoNext = !allPanesPinned && totalGroups > 1;
  const canGoPrevious = !allPanesPinned && totalGroups > 1;

  // Reset W/L, pan, and zoom on all visible viewports (current group only).
  // For video viewports, restores the exact initial camera state saved after
  // setup (including aspect-ratio-corrected dimensions) instead of calling
  // resetCamera(), which can produce a different fit-to-window calculation.
  const resetViewport = useCallback(() => {
    // Reset viewer also clears any active view filters and group selection.
    setSelectedViews([]);
    setSelectedGroupId(null);

    // Return every pane's playback speed to native (multiplier 1), so the
    // cine FPS control re-anchors to the backend-provided rate.
    setCineSpeedByPane(new Map());

    if (!renderingEngine) return;

    deactivateTool();

    // Clear persisted VOI only for the currently visible DICOMs
    assignedDicoms.forEach((d) => d && voiMapRef.current.delete(d.uuid));

    registeredViewportIdsRef.current.forEach((vpId) => {
      try {
        const viewport = renderingEngine.getViewport(vpId) as
          | Types.IStackViewport
          | Types.IVideoViewport
          | undefined;
        if (!viewport) return;

        viewport.setProperties({ voiRange: { lower: 0, upper: 255 } });

        const saved = viewportInitialCameraRef.current.get(vpId);
        const isVideoVp =
          typeof (viewport as Types.IVideoViewport).play === 'function';

        if (saved && isVideoVp) {
          // VIDEO: restore aspect-ratio-corrected dimensions, then reset.
          // Must call resize() first to sync canvas pixel-buffer dimensions
          // with CSS layout dimensions — refreshRenderValues() mixes
          // canvas.offsetHeight (CSS) with canvas.height (pixel buffer)
          // in its fit calculation, so they must be in sync.
          const vp = viewport as unknown as {
            videoWidth: number;
            videoHeight: number;
            resize: () => void;
          };
          if (saved.videoWidth != null) vp.videoWidth = saved.videoWidth;
          if (saved.videoHeight != null) vp.videoHeight = saved.videoHeight;
          // resize() syncs canvas dimensions AND calls refreshRenderValues()
          vp.resize();
          viewport.render();
        } else {
          // STACK (or no saved state): resetCamera is reliable
          viewport.resetCamera();
          viewport.render();
        }
      } catch {
        // Viewport may not be ready
      }
    });
  }, [renderingEngine, deactivateTool, assignedDicoms]);

  // Handle grid layout change — the resize reconcile above reseats the panes
  const handleGridLayoutChange = useCallback((layout: GridLayout) => {
    setGridLayout(layout);
    setActiveViewportId(null);
  }, []);

  // Handle viewport click (select for DICOM loading)
  const handleViewportClick = useCallback(
    (viewportIndex: number) => {
      // Only deactivate ANNOTATION tools when switching viewports.
      // Manipulation tools (W/L, Pan, Zoom) stay active on all viewports —
      // deactivating them triggers a Cornerstone re-render that clears
      // VideoViewport's CSS SVG filter used for W/L brightness/contrast.
      if (
        viewportIndex !== selectedViewportIndex &&
        activeTool &&
        !MANIPULATION_TOOLS.has(activeTool)
      ) {
        deactivateTool();
      }
      setSelectedViewportIndex(viewportIndex);
    },
    [deactivateTool, selectedViewportIndex, activeTool]
  );

  // The single "successful normal navigation" step: move the cursor and refill
  // the unpinned panes from it. Callers resolve blocking before calling.
  const seatCursor = useCallback(
    (cursor: number, list: Dicom[] = filteredSortedDicoms) => {
      setNavCursor(cursor);
      setAssignedDicoms((prev) =>
        fillUnpinnedPanes(prev, viewportCount, pinnedSlots, list, cursor)
      );
    },
    [filteredSortedDicoms, viewportCount, pinnedSlots]
  );

  // Navigation: advance/retreat the cursor by one page (pageSize), wrapping at
  // the ends. After a filmstrip click the cursor may be off a page boundary;
  // paging simply strides from wherever it is. With no unpinned pane the
  // attempt is a true no-op — nothing (cursor included) changes.
  const goToPage = useCallback(
    (direction: 1 | -1) => {
      if (allPanesPinned) {
        reportBlockedNavigation();
        return;
      }
      seatCursor(stepCursor(navigableIndices, navCursor, pageSize, direction));
    },
    [
      allPanesPinned,
      reportBlockedNavigation,
      seatCursor,
      navigableIndices,
      navCursor,
      pageSize,
    ]
  );

  const goToNextGroup = useCallback(() => goToPage(1), [goToPage]);
  const goToPreviousGroup = useCallback(() => goToPage(-1), [goToPage]);

  // Legacy `/studies/<uuid>/<n>` link (browser back/forward, direct link).
  // Returns false when the URL was refused, so the hook points it back at
  // what's on screen.
  const followDicomNumber = (dicomNumber: number): boolean => {
    const dicom = sortedDicoms[dicomNumber - 1];
    // KEY GUARD: already on screen (TO-URL just pushed this URL after a
    // navigation) — only select that pane; re-seating would undo it.
    const visibleSlot = dicom
      ? assignedDicoms.findIndex((d) => d?.uuid === dicom.uuid)
      : -1;
    if (visibleSlot !== -1) {
      setSelectedViewportIndex(visibleSlot);
      return true;
    }

    // Genuine external navigation — re-seat this DICOM as the first unpinned
    // pane (matches a filmstrip click).
    const cursor = dicom
      ? filteredSortedDicoms.findIndex((d) => d.uuid === dicom.uuid)
      : -1;
    if (cursor !== -1 && availablePane !== null) {
      seatCursor(cursor);
      setSelectedViewportIndex(availablePane);
      return true;
    }

    // Refused: out of range, filtered out, or fully pinned grid.
    if (cursor !== -1) reportBlockedNavigation();
    return false;
  };

  // Full `?layout=…&panes=…` URL state: restore layout, pins and panes as-is.
  // Also records the new pane source so the resize reconcile doesn't refill
  // the restored panes.
  const applyPaneState = (layout: GridLayout, resolved: ResolvedPaneState) => {
    setPaneSource({
      dicoms: filteredSortedDicoms,
      viewportCount: layout.rows * layout.cols,
    });
    setGridLayout(layout);
    setPinnedSlots(resolved.pinnedSlots);
    setNavCursor(resolved.navCursor);
    setAssignedDicoms(resolved.paneDicoms);
    setSelectedViewportIndex(resolved.selectedViewportIndex);
  };

  useViewerUrlSync({
    urlState,
    onUrlStateChange,
    sortedDicoms,
    filteredSortedDicoms,
    dicomNumberByUuid,
    gridLayout,
    assignedDicoms,
    pinnedPanes,
    selectedViewportIndex,
    followDicomNumber,
    applyPaneState,
  });

  // Load a specific DICOM by its global index into sortedDicoms. Returns false
  // when nothing happened (unknown DICOM, or blocked by a fully pinned grid).
  const loadDicomByGlobalIndex = useCallback(
    (globalIndex: number): boolean => {
      const dicom = sortedDicoms[globalIndex];
      if (!dicom) return false;

      const pinnedSlot = Array.from(pinnedPanes).find(
        ([, pinned]) => pinned.uuid === dicom.uuid
      )?.[0];

      // Resolve an eligible pane BEFORE touching any state.
      if (pinnedSlot === undefined && availablePane === null) {
        reportBlockedNavigation();
        return false;
      }

      setAutoplayEnabled(settings.autoplay);
      setStartFrame(undefined);

      // Already shown in a pinned pane: focus that pane — not a navigation.
      if (pinnedSlot !== undefined) {
        setSelectedViewportIndex(pinnedSlot);
        return true;
      }

      // Re-seat the target as the first unpinned pane; the following DICOMs
      // fill the remaining unpinned panes.
      const cursor = filteredSortedDicoms.findIndex(
        (d) => d.uuid === dicom.uuid
      );
      if (cursor === -1) {
        // Target is hidden by the active view filter (e.g. a report/annotation
        // jump to a filtered-out DICOM). Clear the filter so the link works and
        // seat the cursor against the unfiltered list the grid will show.
        setSelectedViews([]);
        setSelectedGroupId(null);
        seatCursor(globalIndex, sortedDicoms);
      } else {
        seatCursor(cursor);
      }
      setSelectedViewportIndex(availablePane);
      return true;
    },
    [
      sortedDicoms,
      pinnedPanes,
      availablePane,
      reportBlockedNavigation,
      filteredSortedDicoms,
      seatCursor,
      settings.autoplay,
    ]
  );

  const navigateToDicomByUuid = useCallback(
    (uuid: string): boolean => {
      const index = sortedDicoms.findIndex((d) => d.uuid === uuid);
      return index !== -1 && loadDicomByGlobalIndex(index);
    },
    [sortedDicoms, loadDicomByGlobalIndex]
  );

  // Navigate to a specific DICOM and frame — called on measurement label click.
  // 1. Navigates to the DICOM group
  // 2. Pauses all videos
  // 3. Seeks to the target frame (if viewport exists)
  const goToAnnotation = useCallback(
    (dicomUuid: string, frameNumber: number) => {
      // 1. Navigate to the DICOM group (resets autoplay to default). Blocked
      // (fully pinned grid) → leave playback completely untouched.
      if (!navigateToDicomByUuid(dicomUuid)) return;

      // 2. Pause all videos
      pauseAll();

      // 3. Override autoplay off and set target frame (after navigate, so it wins the batch)
      setAutoplayEnabled(false);
      setStartFrame(frameNumber);

      // 4. Try to seek immediately (works if DICOM is already visible).
      // Skip when the pane is ALREADY on the target frame: a redundant re-seek
      // wobbles the derived frame (N → N-1/0 → N through the video element),
      // and the frame-leave deselect reads that as leaving the annotation's
      // frame — killing the selection a same-frame label switch just made.
      if (renderingEngine) {
        renderingEngine.getViewports().forEach((vp) => {
          try {
            const videoVp = vp as Types.IVideoViewport;
            if (typeof videoVp.setFrameNumber !== 'function') return;
            const imageId = videoVp.getCurrentImageId?.();
            if (
              imageId?.includes(dicomUuid) &&
              !imageId.endsWith(`/frames/${frameNumber}`)
            ) {
              videoVp.setFrameNumber(frameNumber);
            }
          } catch {
            // Viewport may not be ready
          }
        });
      }
    },
    [navigateToDicomByUuid, pauseAll, renderingEngine]
  );

  // AP-773 (R6): the history's way to move the viewer. `seekFrame` moves one
  // pane only — no re-seating — for a DICOM that is already on screen;
  // `goToAnnotation` is the label-click jump for an off-screen one.
  useEffect(
    () =>
      registerViewerNavigator({
        goToAnnotation,
        seekFrame: (viewportId, frameNumber) => {
          pauseAll();
          try {
            const vp = renderingEngine?.getViewport(viewportId) as
              | Types.IVideoViewport
              | undefined;
            if (typeof vp?.setFrameNumber === 'function') {
              vp.setFrameNumber(frameNumber);
            }
          } catch {
            // Viewport may not be ready.
          }
        },
      }),
    [goToAnnotation, pauseAll, renderingEngine]
  );

  // Set the ref so Report (outside this provider) can call goToAnnotation
  if (goToAnnotationRef) {
    // eslint-disable-next-line no-param-reassign
    goToAnnotationRef.current = goToAnnotation;
  }

  // Handle tool change
  // Pauses all videos when measurement tools are activated for accurate measurements
  const handleToolChange = useCallback(
    (tool: string) => {
      if (activeTool === tool) {
        deactivateTool();
        // Also clear the awaiting measurement so useMeasurementToolActivation
        // doesn't immediately re-activate the tool we just turned off.
        dispatch(clearMeasurement());
        return;
      }

      // Pause all videos when any tool is activated
      pauseAll();

      changeTool(tool);
    },
    [activeTool, changeTool, deactivateTool, pauseAll, dispatch]
  );

  // Sync grid layout when settings change from settings page or another tab
  // Skipped on mount so a layout from the URL wins.
  const prevPanelLayoutRef = useRef(settings.panelLayout);
  useEffect(() => {
    if (prevPanelLayoutRef.current === settings.panelLayout) return;
    prevPanelLayoutRef.current = settings.panelLayout;
    const layout = toGridLayout(settings.panelLayout);
    setGridLayout((prev) =>
      prev.rows === layout.rows && prev.cols === layout.cols ? prev : layout
    );
  }, [settings.panelLayout]);

  // Track previous grid layout
  const prevGridLayoutRef = useRef(gridLayout);

  // Handle grid layout changes
  useEffect(() => {
    if (!renderingEngine) return () => {};

    const prevLayout = prevGridLayoutRef.current;
    if (
      prevLayout.rows !== gridLayout.rows ||
      prevLayout.cols !== gridLayout.cols
    ) {
      prevGridLayoutRef.current = gridLayout;

      const timeoutId = window.setTimeout(() => {
        try {
          // A grid layout swap (e.g. 1x4 -> 2x2) does not change the grid
          // container's outer size, so the ResizeObserver in useViewportResize
          // never fires — this is the only resize for that case. Use the same
          // routine as the drag path so stacks both resize their canvas buffers
          // AND refit to the new cell while keeping each pane's pan/zoom; a
          // bare resize() keeps the old world camera, leaving stacks "stuck" at
          // the previous size until a manual drag.
          resizeAndRefitViewports(renderingEngine);
        } catch {
          // Resize may fail if rendering engine is being destroyed
        }
      }, 50);

      return () => window.clearTimeout(timeoutId);
    }
    return () => {};
  }, [gridLayout, renderingEngine]);

  // Pin a slot — protects it from normal navigation. Content stays as is.
  const pinPane = useCallback((paneIndex: number) => {
    setPinnedSlots((prev) => new Set(prev).add(paneIndex));
  }, []);

  // Unpin a slot — it keeps its DICOM until the next navigation replaces it.
  const unpinPane = useCallback((paneIndex: number) => {
    setPinnedSlots((prev) => {
      const next = new Set(prev);
      next.delete(paneIndex);
      return next;
    });
  }, []);

  // Shift+drag placements waiting for their pane to load, with what the pane
  // showed before, so a failed load can put the pane back.
  const pendingPlacementsRef = useRef(
    new Map<
      number,
      { dicomUuid: string; previous: Dicom | null; wasPinned: boolean }
    >()
  );

  // Shift+drag direct placement: the user chose both the DICOM and the pane,
  // so a pinned pane may be replaced. Only the target pane changes; it becomes
  // active and (re)pinned. The navigation cursor is untouched.
  const placeDicomInPane = useCallback(
    (paneIndex: number, dicom: Dicom) => {
      pendingPlacementsRef.current.set(paneIndex, {
        dicomUuid: dicom.uuid,
        previous: assignedDicoms[paneIndex] ?? null,
        wasPinned: pinnedSlots.has(paneIndex),
      });
      setAssignedDicoms((prev) => {
        const next = [...prev];
        next[paneIndex] = dicom;
        return next;
      });
      setPinnedSlots((prev) => new Set(prev).add(paneIndex));
      handleViewportClick(paneIndex);
    },
    [handleViewportClick, assignedDicoms, pinnedSlots]
  );

  // A placed DICOM that fails to load leaves the pane as it was: previous
  // DICOM and pin state restored. The cursor was never moved.
  const reportPaneLoadResult = useCallback(
    (paneIndex: number, dicomUuid: string, loaded: boolean) => {
      const pending = pendingPlacementsRef.current.get(paneIndex);
      if (pending?.dicomUuid !== dicomUuid) return;
      pendingPlacementsRef.current.delete(paneIndex);
      if (loaded || assignedDicoms[paneIndex]?.uuid !== dicomUuid) return;

      setAssignedDicoms((prev) => {
        const next = [...prev];
        next[paneIndex] = pending.previous;
        return next;
      });
      if (!pending.wasPinned) {
        setPinnedSlots((prev) => {
          const next = new Set(prev);
          next.delete(paneIndex);
          return next;
        });
      }
    },
    [assignedDicoms]
  );

  const getDicomByUuid = useCallback(
    (uuid: string) => sortedDicoms.find((d) => d.uuid === uuid),
    [sortedDicoms]
  );

  // Compute clamped selected viewport index during render (React 19+ pattern)
  // This avoids cascading renders from calling setState in an effect
  const clampedSelectedViewportIndex = useMemo(() => {
    if (assignedDicoms.length === 0) return 0;
    return Math.min(selectedViewportIndex, assignedDicoms.length - 1);
  }, [selectedViewportIndex, assignedDicoms.length]);

  // Context value
  const contextValue: ViewerContextValue = useMemo(
    () => ({
      studyUuid,
      renderingEngine,
      renderingEngineId,
      activeViewportId,
      setActiveViewportId,
      selectedViewportIndex: clampedSelectedViewportIndex,
      setSelectedViewportIndex,
      currentGroupIndex,
      totalGroups,
      viewportCount,
      goToNextGroup,
      goToPreviousGroup,
      loadDicomByGlobalIndex,
      navigateToDicomByUuid,
      goToAnnotation,
      canGoNext,
      canGoPrevious,
      activeTool,
      changeTool,
      deactivateTool,
      setupToolGroupForViewport,
      cleanupToolGroupForViewport,
      registeredViewportIds,
      registerViewportId,
      unregisterViewportId,
      viewportIds,
      // Expose these for MultiViewerContainer
      gridLayout,
      setGridLayout: handleGridLayoutChange,
      handleViewportClick,
      handleToolChange,
      assignedDicoms,
      getPaneCineSpeed,
      setPaneCineSpeed,
      getPaneNativeFps,
      setPaneNativeFps,
      autoplay: autoplayEnabled,
      startFrame,
      setAutoplayEnabled,
      setStartFrame,
      isInitialized,
      engineError,
      voiMapRef,
      viewportInitialCameraRef,
      resetViewport,
      pinnedPanes,
      allPanesPinned,
      pinPane,
      unpinPane,
      placeDicomInPane,
      reportPaneLoadResult,
      getDicomByUuid,
      dicomNumberByUuid,
      activeDicomUuids,
      filteredSortedDicoms,
      availableViews,
      selectedViews,
      setSelectedViews,
      selectedGroupId,
      setSelectedGroupId,
    }),
    [
      studyUuid,
      renderingEngine,
      renderingEngineId,
      activeViewportId,
      clampedSelectedViewportIndex,
      currentGroupIndex,
      totalGroups,
      viewportCount,
      goToNextGroup,
      goToPreviousGroup,
      loadDicomByGlobalIndex,
      navigateToDicomByUuid,
      goToAnnotation,
      canGoNext,
      canGoPrevious,
      activeTool,
      changeTool,
      deactivateTool,
      setupToolGroupForViewport,
      cleanupToolGroupForViewport,
      registeredViewportIds,
      registerViewportId,
      unregisterViewportId,
      viewportIds,
      gridLayout,
      handleGridLayoutChange,
      handleViewportClick,
      handleToolChange,
      assignedDicoms,
      getPaneCineSpeed,
      setPaneCineSpeed,
      getPaneNativeFps,
      setPaneNativeFps,
      autoplayEnabled,
      startFrame,
      isInitialized,
      engineError,
      resetViewport,
      pinnedPanes,
      allPanesPinned,
      pinPane,
      unpinPane,
      placeDicomInPane,
      reportPaneLoadResult,
      getDicomByUuid,
      dicomNumberByUuid,
      activeDicomUuids,
      filteredSortedDicoms,
      availableViews,
      selectedViews,
      selectedGroupId,
    ]
  );

  // Playback context value (simplified - only video signal functions)
  const playbackContextValue: PlaybackContextValue = useMemo(
    () => ({
      subscribeToVideoPause,
      subscribeToVideoToggle,
      pauseAll,
      togglePlayPauseAll,
    }),
    [
      subscribeToVideoPause,
      subscribeToVideoToggle,
      pauseAll,
      togglePlayPauseAll,
    ]
  );

  return (
    <ViewerContext.Provider value={contextValue}>
      <PlaybackContext.Provider value={playbackContextValue}>
        <ViewerLoadingProvider>{children}</ViewerLoadingProvider>
      </PlaybackContext.Provider>
    </ViewerContext.Provider>
  );
}
